import type { NamePath } from 'rc-field-form/es/interface';

export const DEFAULT_COLLAPSE_OVERVIEW_KEY = 'overview';

export type Opening = {
  bar?: number;
  booked: number;
  date: string;
  discountPrice?: number;
  hasWarning?: boolean;
  hotelId: number;
  id?: number;
  price: number;
  published?: boolean;
  remaining: number;
  roomId: number;
  stock: number;
  errorStatus?: OpeningErrors[];
  name?: string;
  updatedAt?: string;
  isForced: boolean;
  closed: boolean;
  autoDiscount?: boolean;
};

export type FormPackagePrice = {
  packageId?: number;
  packagePriceId?: number;
  published: boolean;
  closed: boolean;
  discountPrice?: number;
  price?: number;
  discount?: number;
  isForced: boolean;
  autoDiscount: boolean;
  lastMinuteAutoDiscount: boolean;
  updatedAt: string;
  errors: OpeningErrors[];
};

export type FormInventoryPlanning = {
  openingId?: number;
  date: string;
  remainingStock: number;
  booked: number;
  bar?: number;
  autoStock: boolean;
  autoBar: boolean;
  packagePrices: FormPackagePrice[];
};

export type FormInventorySaved = {
  packages: {
    packageId: number;
    maxBookings: {
      date: string;
      maxBooking?: number;
    }[];
  }[];
  rooms: {
    roomId: number;
    inventories: FormInventoryPlanning[];
  }[];
};

export type FormPlanning = {
  openingId?: number;
  date: string;
  stock: number;
  booked: number;
  bar?: number;
  autoStock: boolean;
  autoBar: boolean;
  closed: boolean;
  packagePrices: {
    [packageId: string]: FormPackagePrice;
  };
  errors: OpeningErrors[];
};

export type FormRoom = {
  roomId: number;
  plannings: {
    [date: string]: FormPlanning;
  };
};

export type FormPackageBooking = {
  date: string;
  hasMaxBooking: boolean;
  maxBooking?: number;
  booked?: number;
  totalStocks: number;
};

export type FormPackage = {
  packageId: number;
  bookings: {
    [date: string]: FormPackageBooking;
  };
};

export type FormType = {
  rooms: { [key: number]: FormRoom };
  packages: {
    [key: number]: FormPackage;
  };
};

export type TimePeriod = {
  startDate: Date;
  endDate: Date;
};

export type RowData = {
  key: string;
  rowTitle: string;
  [date: string]:
    | {
        booked?: number;
        maxBooking?: number;
        price?: number;
        crossedRate?: number;
      }
    | string
    | number;
};

export type DuplicateType = {
  property: string;
  value: number;
  day: Date;
  modeData: DuplicationModeData;
  roomId?: number;
  packageId?: number;
  nameField: NamePath;
};

export type ErrorsListType = {
  errorType: OpeningErrors;
  openings: {
    roomId: number;
    packageId?: number;
    date: string;
  }[];
};

export enum DuplicationMode {
  END_OF_WEEK = 'END_OF_WEEK',
  END_OF_NEXT_WEEK = 'END_OF_NEXT_WEEK',
  END_OF_TABLE = 'END_OF_TABLE',
  CUSTOM = 'CUSTOM',
}

export type DuplicationModeData =
  | {
      mode:
        | DuplicationMode.END_OF_WEEK
        | DuplicationMode.END_OF_NEXT_WEEK
        | DuplicationMode.END_OF_TABLE;
    }
  | {
      mode: DuplicationMode.CUSTOM;
      customDay: Date;
    };

export enum OpeningStatus {
  VALID = 'VALID',
  PUBLISHED = 'PUBLISHED',
  REJECTED = 'REJECTED',
  UNAVAILABLE = 'UNAVAILABLE',
  CLOSED = 'CLOSED',
  CLOSED_ON_STAYCATION = 'CLOSED_ON_STAYCATION',
  UNKNOWN = 'UNKNOWN',
}

export enum OpeningErrors {
  MISSING_STOCK = 'MISSING_STOCK',
  WRONG_STOCK = 'WRONG_STOCK',
  EXCEED_RATE = 'EXCEED_RATE',
  MISSING_BAR = 'MISSING_BAR',
  MISSING_DISCOUNT_PRICE = 'MISSING_DISCOUNT_PRICE',
  UNDER_MINIMUM_PRICE = 'UNDER_MINIMUM_PRICE',
  NO_DISCOUNT = 'NO_DISCOUNT',
  CLOSED = 'CLOSED',
  REJECTED_BY_AM = 'REJECTED_BY_AM',
}

export const OpeningsErrorMessage = {
  [OpeningErrors.MISSING_STOCK]: 'Stock missing',
  [OpeningErrors.WRONG_STOCK]: 'Remaining stock above maximum room capacity',
  [OpeningErrors.EXCEED_RATE]: 'Staycation rate above maximum accepted',
  [OpeningErrors.MISSING_BAR]: 'BAR RO missing',
  [OpeningErrors.MISSING_DISCOUNT_PRICE]: 'Staycation rate missing',
  [OpeningErrors.UNDER_MINIMUM_PRICE]: 'Staycation rate under minimum accepted',
  [OpeningErrors.NO_DISCOUNT]: 'No discount',
  [OpeningErrors.CLOSED]: 'Closed on your channel manager',
  [OpeningStatus.PUBLISHED]: 'Published',
  [OpeningStatus.CLOSED_ON_STAYCATION]:
    'This date is not yet for sale on Staycation',
  [OpeningErrors.REJECTED_BY_AM]: 'Rejected by account manager',
};
